/**
 * ECOSMITH Hero Component
 * Full-viewport background video with gradient overlay
 */

import { gsap } from 'gsap';

class EcoHero extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this.render();
    this.initAnimations();
    this.initVideo();
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        /* ── Self-hosted fonts (must be declared inside Shadow DOM) ── */
        @font-face {
          font-family: 'Space Age';
          src: url('/webfonts_SpaceAge/SpaceAge.ttf.eot');
          src: url('/webfonts_SpaceAge/SpaceAge.ttf.eot?#iefix') format('embedded-opentype'),
               url('/webfonts_SpaceAge/SpaceAge.ttf.woff') format('woff'),
               url('/webfonts_SpaceAge/SpaceAge.ttf.svg#SpaceAge') format('svg');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'Stopmotion';
          src: url('/webfonts_Stopmotion/Stopmotion.ttf.eot');
          src: url('/webfonts_Stopmotion/Stopmotion.ttf.eot?#iefix') format('embedded-opentype'),
               url('/webfonts_Stopmotion/Stopmotion.ttf.woff') format('woff'),
               url('/webfonts_Stopmotion/Stopmotion.ttf.svg#Stopmotion') format('svg');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }

        :host {
          display: block;
          position: relative;
          width: 100%;
          height: 100vh;
          min-height: 600px;
          overflow: hidden;
        }

        .hero-container {
          position: relative;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Video Background */
        .video-background {
          position: absolute;
          inset: 0;
          z-index: 0;
        }

        .video-background video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .video-background::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(
            180deg,
            rgba(10, 26, 15, 0.45) 0%,
            rgba(10, 26, 15, 0.65) 50%,
            rgba(10, 26, 15, 0.85) 100%
          );
        }

        /* Radial glow overlay — no diagonal texture */
        .gradient-overlay {
          position: absolute;
          inset: 0;
          z-index: 1;
          background:
            radial-gradient(ellipse 70% 60% at 25% 30%, rgba(74, 222, 128, 0.08) 0%, transparent 60%),
            radial-gradient(ellipse 50% 40% at 75% 75%, rgba(34, 197, 94, 0.06) 0%, transparent 55%);
          pointer-events: none;
        }

        /* Particle dots — top-right quadrant */
        .particles {
          position: absolute;
          top: 0; right: 0;
          width: 50%; height: 60%;
          z-index: 2;
          pointer-events: none;
        }
        .particle {
          position: absolute;
          border-radius: 50%;
          background: #4ade80;
          animation: hero-dot-pulse 3s ease-in-out infinite;
        }
        .p1 { width: 4px; height: 4px; top: 15%; right: 20%; opacity: 0.7; animation-delay: 0s; }
        .p2 { width: 3px; height: 3px; top: 28%; right: 35%; opacity: 0.5; animation-delay: 0.7s; }
        .p3 { width: 5px; height: 5px; top: 10%; right: 48%; opacity: 0.6; animation-delay: 1.4s; }
        .p4 { width: 3px; height: 3px; top: 42%; right: 15%; opacity: 0.4; animation-delay: 2.1s; }
        .p5 { width: 4px; height: 4px; top: 22%; right: 58%; opacity: 0.5; animation-delay: 1.0s; }
        @keyframes hero-dot-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.9); opacity: 0.15; }
        }

        /* Content */
        .hero-content {
          position: relative;
          z-index: 2;
          text-align: center;
          padding: 2rem;
          max-width: 900px;
          margin: 0 auto;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 16px;
          background: rgba(74, 222, 128, 0.08);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(74, 222, 128, 0.3);
          border-radius: 9999px;
          color: #4ade80;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          margin-bottom: 2rem;
          opacity: 0;
          transform: translateY(20px);
        }

        .hero-badge svg {
          width: 14px;
          height: 14px;
          color: #4ade80;
        }

        .hero-headline {
          font-family: 'IntroRust', 'Space Grotesk', system-ui, sans-serif;
          font-size: clamp(2.5rem, 6vw, 5rem);
          font-weight: 700;
          text-transform: uppercase;
          line-height: 1.1;
          letter-spacing: 0.02em;
          color: #f0faf4;
          margin: 0 0 1.5rem 0;
          word-break: normal;
          overflow-wrap: normal;
          hyphens: none;
          opacity: 0;
          transform: translateY(30px);
        }

        .hero-headline span {
          color: #4ade80;
          -webkit-text-fill-color: #4ade80;
        }

        .hero-subhead {
          font-size: clamp(1rem, 2vw, 1.25rem);
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.8);
          max-width: 600px;
          margin: 0 auto 2.5rem auto;
          opacity: 0;
          transform: translateY(20px);
        }

        /* CTA Buttons */
        .hero-ctas {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          justify-content: center;
          opacity: 0;
          transform: translateY(20px);
        }

        .btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 16px 32px;
          background: #4ade80;
          color: #0a1a0f;
          text-decoration: none;
          font-size: 1rem;
          font-weight: 700;
          border-radius: 9999px;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(74, 222, 128, 0.35);
          position: relative;
          overflow: hidden;
        }

        .btn-primary:hover {
          background: #22c55e;
          transform: translateY(-2px);
          box-shadow: 0 8px 28px rgba(74, 222, 128, 0.45);
        }

        .btn-primary svg {
          width: 18px;
          height: 18px;
          transition: transform 0.3s ease;
        }

        .btn-primary:hover svg {
          transform: translateX(4px);
        }

        .btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 16px 32px;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          color: white;
          text-decoration: none;
          font-size: 1rem;
          font-weight: 600;
          border-radius: 9999px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .btn-secondary:hover {
          background: rgba(255, 255, 255, 0.2);
          border-color: rgba(255, 255, 255, 0.3);
        }

        .btn-secondary svg {
          width: 18px;
          height: 18px;
        }

        /* Scroll Indicator */
        .scroll-indicator {
          position: absolute;
          bottom: 40px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          color: rgba(255, 255, 255, 0.6);
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          opacity: 0;
        }

        .scroll-indicator svg {
          width: 24px;
          height: 24px;
          animation: bounce 2s infinite;
        }

        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
          60% { transform: translateY(-5px); }
        }

        /* Stats Bar */
        .stats-bar {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 2;
          display: flex;
          justify-content: center;
          gap: 4rem;
          padding: 2rem;
          background: linear-gradient(to top, rgba(10, 14, 20, 0.9) 0%, transparent 100%);
          opacity: 0;
        }

        .stat-item {
          text-align: center;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 700;
          color: white;
          font-family: 'JetBrains Mono', monospace;
          tabular-nums;
        }

        .stat-label {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.6);
          margin-top: 4px;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .stats-bar {
            gap: 2rem;
            padding: 1.5rem 1rem;
          }

          .stat-value {
            font-size: 1.5rem;
          }

          .stat-label {
            font-size: 0.75rem;
          }
        }

        @media (max-width: 480px) {
          .hero-ctas {
            flex-direction: column;
            width: 100%;
            padding: 0 1rem;
          }

          .btn-primary,
          .btn-secondary {
            width: 100%;
            justify-content: center;
          }
        }
      </style>

      <section class="hero-container" aria-label="Hero">
        <div class="video-background">
          <video
            autoplay
            muted
            loop
            playsinline
            aria-hidden="true"
          >
            <source src="/HERO_bg.mp4" type="video/mp4">
          </video>
        </div>

        <div class="gradient-overlay"></div>

        <!-- Particle dots -->
        <div class="particles" aria-hidden="true">
          <span class="particle p1"></span>
          <span class="particle p2"></span>
          <span class="particle p3"></span>
          <span class="particle p4"></span>
          <span class="particle p5"></span>
        </div>

        <div class="hero-content">
          <div class="hero-badge">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            ISO 9001:2015 Certified
          </div>

          <h1 class="hero-headline">
            <span>Hygienic Engineering</span><br>
            for Food Safety
          </h1>

          <p class="hero-subhead">
            Precision-engineered processing equipment and CIP/SIP automation systems 
            that meet the highest standards of hygiene and compliance for Pakistan's food industry.
          </p>

          <div class="hero-ctas">
            <button class="btn-primary" data-action="explore">
              Explore Solutions
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </button>
            <button class="btn-secondary" data-action="contact">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              Request Consultation
            </button>
          </div>
        </div>

        <div class="scroll-indicator">
          <span>Scroll to explore</span>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 13l5 5 5-5M7 6l5 5 5-5"/></svg>
        </div>

        <div class="stats-bar">
          <div class="stat-item">
            <div class="stat-value">15+</div>
            <div class="stat-label">Years Experience</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">200+</div>
            <div class="stat-label">Installations</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">50+</div>
            <div class="stat-label">Team Members</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">99%</div>
            <div class="stat-label">Client Satisfaction</div>
          </div>
        </div>
      </section>
    `;
  }

  initAnimations() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      // Show content immediately without animation
      this.shadowRoot.querySelectorAll('.hero-badge, .hero-headline, .hero-subhead, .hero-ctas, .scroll-indicator, .stats-bar')
        .forEach(el => {
          el.style.opacity = '1';
          el.style.transform = 'none';
        });
      return;
    }

    // GSAP entrance animations
    const tl = gsap.timeline({ delay: 0.3 });

    tl.to(this.shadowRoot.querySelector('.hero-badge'), {
      opacity: 1,
      y: 0,
      duration: 0.6,
      ease: 'power2.out'
    })
      .to(this.shadowRoot.querySelector('.hero-headline'), {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power2.out'
      }, '-=0.3')
      .to(this.shadowRoot.querySelector('.hero-subhead'), {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: 'power2.out'
      }, '-=0.4')
      .to(this.shadowRoot.querySelector('.hero-ctas'), {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: 'power2.out'
      }, '-=0.3')
      .to(this.shadowRoot.querySelector('.scroll-indicator'), {
        opacity: 1,
        duration: 0.6,
        ease: 'power2.out'
      }, '-=0.2')
      .to(this.shadowRoot.querySelector('.stats-bar'), {
        opacity: 1,
        duration: 0.8,
        ease: 'power2.out'
      }, '-=0.4');
  }

  initVideo() {
    const video = this.shadowRoot.querySelector('video');
    if (!video) return;
    video.play().catch(err => {
      console.warn('[EcoHero] Video autoplay prevented:', err);
    });
  }
}

customElements.define('eco-hero', EcoHero);